/**
 * View-Feature Mapping Configuration
 *
 * Defines which features are required/used by each view.
 * Used to activate features when views mount and deactivate when they unmount.
 *
 * Features:
 * - timeline: Activity timeline tracking
 * - chat: Real-time chat between frontend and desktop
 * - config-manager: Configuration management
 */

export const VIEW_FEATURES = {
  // Monitoring views
  'monitoring/sacred-timeline': [
    'sacred-timeline'
  ],

  'monitoring/dossier': [
    'dossier'
  ],

  'monitoring/discord': [
    'discord-monitor'
  ],

  'monitoring/domain-heatmap': [
    'sacred-timeline'
  ],
  'monitoring/keyword-dashboard': [
    'sacred-timeline'
  ],
  'monitoring/tab-surveillance': [
    'tab-surveillance'
  ],

  // Communication views
  'communication/chat': [
    'chat'
  ],
  'communication/ai-voice': [
    'ai-voice'
  ],

  // System views
  'system/configuration': [
    'config-manager',
    'consent'
  ],
  'system/debug': [
    'timeline'  // Debug page uses timeline for database management
  ],

  'communication/interrogation': [
    'interrogation'
  ],

  // Punishment views
  'punishment/economy': [
    'economy'
  ],
  'punishment/spin-wheel': [
    'spin-wheel'
  ],
  'punishment/write-lines': [
    'write-lines'
  ],
  'punishment/sound-blaster': [
    'sound-blaster'
  ],
  'punishment/window-rearrange': [
    'window-rearrange'
  ],
  'punishment/countdown-timer': [
    'countdown-timer'
  ],
  'punishment/phantom-cursor': [
    'phantom-cursor'
  ],
  'punishment/wallpaper-hijack': [
    'wallpaper-hijack'
  ],
  'punishment/quiz': [
    'quiz'
  ],
  'communication/quick-confession': [
    'quick-confession'
  ],
  'communication/journal': [
    'journal'
  ],
  'communication/user-panel': [
    'user-panel',
    'economy'
  ],

  // Integration views
  'integrations/anthropic': [
    'ai-config'
  ],
  'integrations/pishock': [
    'pishock',
    'collar-compliance'
  ],
  'integrations/gaze-tracking': [
    'gaze-tracking'
  ],
  'integrations/cameras': [
    'camera-monitor'
  ],

  // Games views
  'games/labyrinth': [
    'labyrinth',
    'economy'
  ],
  'games/task-board': [
    'task-board',
    'economy'
  ],
  'games/card-war': [
    'card-war'
  ],
  'games/blackjack': [
    'blackjack'
  ],
  'games/dungeon-crawler': [
    'dungeon-crawler'
  ],
  'games/battleship': [
    'battleship'
  ],
  'games/go-fish': [
    'go-fish'
  ],
  'games/challenges': [
    'challenges'
  ],
  'games/word-search': [
    'word-search'
  ],
  'games/hangman': [
    'hangman'
  ],
  'games/chess': [
    'chess'
  ],
  'games/memory-match': [
    'memory-match'
  ],
  'games/jigsaw': [
    'jigsaw'
  ],
  'punishment/eye-lock': [
    'eye-lock'
  ],
  'punishment/screen-tint': [
    'screen-tint'
  ],
  'punishment/auction-house': [
    'economy'
  ],

  // Mobile device views removed from sidebar
};

/**
 * Get features required for a view
 * @param {string} viewPath - View path (e.g., 'monitoring/sacred-timeline')
 * @returns {Array<string>} Feature IDs required for the view
 */
export function getFeaturesForView(viewPath) {
  return VIEW_FEATURES[viewPath] || [];
}

/**
 * Get all unique features across all views
 * @returns {Array<string>} All feature IDs
 */
export function getAllFeatures() {
  const allFeatures = new Set();
  Object.values(VIEW_FEATURES).forEach(features => {
    features.forEach(feature => allFeatures.add(feature));
  });
  return Array.from(allFeatures);
}

/**
 * Get views that use a specific feature
 * @param {string} featureId - Feature ID
 * @returns {Array<string>} View paths that use the feature
 */
export function getViewsForFeature(featureId) {
  return Object.entries(VIEW_FEATURES)
    .filter(([_, features]) => features.includes(featureId))
    .map(([viewPath]) => viewPath);
}
